import { forwardRef, useState, type InputHTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";
import { Eye, EyeOff } from "@/shared/components/icons";
import { messages } from "@/shared/i18n";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | undefined;
  helperText?: string;
  fullWidth?: boolean;
  "data-testid"?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = true,
      className,
      id,
      type,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const isPasswordField = type === "password";
    const effectiveType = isPasswordField ? (isPasswordVisible ? "text" : "password") : type;

    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    const errorId = inputId ? `${inputId}-error` : undefined;
    const hasError = Boolean(error);

    const handleTogglePasswordVisibility = () => {
      setIsPasswordVisible((prev) => !prev);
    };

    return (
      <div className={cn(fullWidth && "w-full")}>
        {label && (
          <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor={inputId}>
            {label}
            {props.required && (
              <span aria-label="required" className="text-red-600">
                *
              </span>
            )}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            aria-describedby={hasError ? errorId : undefined}
            aria-invalid={hasError}
            className={cn(
              "block w-full rounded-lg border px-3 py-2 shadow-sm transition-colors",
              "bg-white text-gray-900",
              "placeholder-gray-400",
              "focus:border-brand-primary focus:ring-brand-primary focus:ring-1 focus:outline-none",
              "disabled:cursor-not-allowed disabled:bg-gray-100",
              hasError
                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300",
              isPasswordField && "pr-10",
              className
            )}
            data-testid={dataTestId}
            id={inputId}
            type={effectiveType}
            {...props}
          />
          {isPasswordField && (
            <button
              aria-label={
                isPasswordVisible
                  ? messages.components.accessibility.hidePassword
                  : messages.components.accessibility.showPassword
              }
              className="focus:ring-brand-primary absolute top-1/2 right-2 -translate-y-1/2 rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:ring-2 focus:ring-offset-1 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
              data-testid={dataTestId ? `${dataTestId}-toggle-password` : undefined}
              type="button"
              onClick={handleTogglePasswordVisibility}
            >
              {isPasswordVisible ? (
                <EyeOff aria-hidden className="h-5 w-5" />
              ) : (
                <Eye aria-hidden className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
        {(error ?? helperText) && errorId && (
          <p
            className={cn("mt-1 text-sm", hasError ? "text-red-600" : "text-gray-500")}
            data-testid={dataTestId ? `${dataTestId}-error` : undefined}
            id={errorId}
          >
            {error ?? helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
